import { useMemo, useState } from 'react'
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from '@/constants/pagination'
import { OfflinePatient, safelyConvertDateToString } from '@/core/offline'

export type FilterState = {
  searchText: string
  page: number
  itemsPerPage: number
}

export function usePatients(patients: OfflinePatient[]) {
  const [filter, setFilter] = useState<FilterState>({
    searchText: '',
    page: 1,
    itemsPerPage: DEFAULT_ROWS_PER_PAGE_OPTIONS[0],
  })

  const filteredPatients = useFilterPatients(patients, filter.searchText)
  const paginatedPatients = usePaginatePatients(filteredPatients, filter.page, filter.itemsPerPage)

  const formattedPatients = useMemo(
    () =>
      paginatedPatients.map((patient) => ({
        _id: patient._offlineId || patient._id,
        name: patient.userInfo.name,
        surname: patient.userInfo.surname,
        gender: patient.userInfo.gender,
        birthday: safelyConvertDateToString(patient.userInfo.birthday),
        city: patient.contactInfo?.city || '',
        secu: patient.secu || '',
        syncStatus: patient._syncStatus,
      })),
    [paginatedPatients]
  )

  return {
    patients: formattedPatients,
    page: filter.page,
    itemsPerPage: filter.itemsPerPage,
    rowsPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS,
    setFilter,
    totalPatients: filteredPatients.length,
  }
}

function useFilterPatients(patients: OfflinePatient[], searchText: string) {
  return useMemo(
    () =>
      patients.filter(
        (patient) =>
          patient.userInfo.name.toLowerCase().includes(searchText) ||
          patient.userInfo.surname.toLowerCase().includes(searchText) ||
          patient.secu?.toLowerCase().includes(searchText)
      ),
    [patients, searchText]
  )
}

function usePaginatePatients(patients: OfflinePatient[], page: number, itemsPerPage: number) {
  return useMemo(() => patients.slice((page - 1) * itemsPerPage, page * itemsPerPage), [patients, page, itemsPerPage])
}
